import { HttpMethods, makeRequest, type MakeRequest } from '../helpers/http.helper';
import { ORG_ID_HEADER } from '../auth/headers';
import type {
  ApprovalRequestDetailSchema,
  ApprovalRequestSchema,
  ApproveApprovalPayload,
  GenericResponse,
  ListApprovalsParams,
  PaginationResponse,
  RejectApprovalPayload,
  SubmitApprovalPayload,
} from '../types/approval.type';

const BASE_PATH = '/approvals';

function unwrapResult<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'result' in raw) {
    return (raw as GenericResponse<T>).result;
  }
  return raw as T;
}

function unwrapList<T>(raw: unknown): PaginationResponse<T> {
  if (raw && typeof raw === 'object' && 'result_list' in raw) {
    return raw as PaginationResponse<T>;
  }
  return { result_list: [], total_count: 0 };
}

function orgHeaders(organizationId: string): MakeRequest['headers'] {
  return { [ORG_ID_HEADER]: organizationId } as unknown as MakeRequest['headers'];
}

export async function listOrgApprovals(
  organizationId: string,
  params: ListApprovalsParams = {},
): Promise<PaginationResponse<ApprovalRequestSchema>> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/org/${organizationId}`,
    method: HttpMethods.GET,
    params,
    headers: orgHeaders(organizationId),
  });
  return unwrapList<ApprovalRequestSchema>(raw);
}

export async function submitOrgApproval(
  organizationId: string,
  payload: SubmitApprovalPayload,
): Promise<ApprovalRequestSchema> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/org/${organizationId}/submit`,
    method: HttpMethods.POST,
    data: payload,
    headers: orgHeaders(organizationId),
  });
  return unwrapResult<ApprovalRequestSchema>(raw);
}

export async function getApproval(
  id: string,
  organizationId?: string,
): Promise<ApprovalRequestDetailSchema> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/${id}`,
    method: HttpMethods.GET,
    headers: organizationId ? orgHeaders(organizationId) : undefined,
  });
  return unwrapResult<ApprovalRequestDetailSchema>(raw);
}

export async function approveApproval(
  id: string,
  payload: ApproveApprovalPayload = {},
  organizationId?: string,
): Promise<ApprovalRequestSchema> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/${id}/approve`,
    method: HttpMethods.POST,
    data: payload,
    headers: organizationId ? orgHeaders(organizationId) : undefined,
  });
  return unwrapResult<ApprovalRequestSchema>(raw);
}

export async function rejectApproval(
  id: string,
  payload: RejectApprovalPayload,
  organizationId?: string,
): Promise<ApprovalRequestSchema> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/${id}/reject`,
    method: HttpMethods.POST,
    data: payload,
    headers: organizationId ? orgHeaders(organizationId) : undefined,
  });
  return unwrapResult<ApprovalRequestSchema>(raw);
}

export async function cancelApproval(
  id: string,
  organizationId?: string,
): Promise<ApprovalRequestSchema> {
  const raw = await makeRequest({
    url: `${BASE_PATH}/${id}/cancel`,
    method: HttpMethods.POST,
    data: {},
    headers: organizationId ? orgHeaders(organizationId) : undefined,
  });
  return unwrapResult<ApprovalRequestSchema>(raw);
}
